import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { resizeImageToWidth } from '../lib/imageUtils';

const useEventTypeSubmission = ({ type, fetchEvents, onNotify }) => {
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  useEffect(() => {
    setUploadProgress(0);
    setUploadStatus('');
  }, [type]);

  const generateToken = () => {
    const arr = new Uint8Array(12);
    if (globalThis.crypto?.getRandomValues) {
      globalThis.crypto.getRandomValues(arr);
      return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
    }

    const fallback = `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random()
      .toString(16)
      .slice(2)}`;
    return fallback.replace(/[^a-f0-9]/gi, '').slice(0, 24).padEnd(24, '0');
  };

  const getFileExt = (file) => {
    const t = String(file?.type || '').toLowerCase();
    if (t === 'image/webp') return 'webp';
    if (t === 'image/png') return 'png';
    if (t === 'image/jpeg' || t === 'image/jpg') return 'jpg';
    if (t === 'image/gif') return 'gif';
    return 'jpg';
  };

  const resolveCoverUrl = ({ coverSelection, finalUrls, uploadedById }) => {
    if (coverSelection?.kind === 'existing') {
      const url = String(coverSelection?.value || '').trim();
      if (url && finalUrls.includes(url)) return url;
    }
    if (coverSelection?.kind === 'draft') {
      const id = String(coverSelection?.value || '').trim();
      const url = uploadedById.get(id);
      if (url && finalUrls.includes(url)) return url;
    }
    return finalUrls[0] || null;
  };

  const resolveCoverFile = ({ coverSelection, coverUrl, uploaded, finalUrls }) => {
    if (!coverUrl) return null;
    if (coverSelection?.kind === 'draft') {
      const id = String(coverSelection?.value || '').trim();
      const hit = uploaded.find((u) => String(u?.id || '').trim() === id);
      return hit?.file || null;
    }
    const match = uploaded.find((u) => String(u?.url || '').trim() === String(coverUrl || '').trim());
    if (match?.file) return match.file;
    const first = uploaded[0];
    if (first?.file && finalUrls?.[0] && String(finalUrls[0]).trim() === String(first.url || '').trim()) {
      return first.file;
    }
    return null;
  };

  const submitEventType = useCallback(
    async ({ payload, generateToken: generateTokenFromCaller, images }) => {
      try {
        if (submitting) return { ok: false, reason: 'submitting' };
        setSubmitting(true);

        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;
        if (!userId) {
          onNotify?.({ title: '로그인 필요', message: '다시 로그인 후 시도해주세요.' }) ||
            alert('로그인이 필요합니다. 다시 로그인 후 시도해주세요.');
          setSubmitting(false);
          return { ok: false, reason: 'unauthenticated' };
        }

        const gen = typeof generateTokenFromCaller === 'function' ? generateTokenFromCaller : generateToken;
        const insertPayload = {
          client_id: userId,
          token: gen(),
          status: 'open',
          registered: 0,
          participants: [],
          ...payload,
          event_type: String(type || '').trim() || 'open_game',
        };

        const { data: created, error } = await supabase.from('session_invites').insert(insertPayload).select('id').single();
        if (error || !created?.id) {
          const msg = error?.message || '등록에 실패했습니다.';
          onNotify?.({ title: '등록 실패', message: msg }) || alert(msg);
          setSubmitting(false);
          return { ok: false, reason: 'insert_failed' };
        }

        const drafts = Array.isArray(images?.drafts) ? images.drafts : [];
        const keptUrls = Array.isArray(images?.keptUrls) ? images.keptUrls : [];
        const coverSelection = images?.coverSelection || null;

        if (drafts.length + keptUrls.length > 10) {
          onNotify?.({ title: '이미지 제한', message: '이미지는 최대 10장까지 등록할 수 있습니다.' }) ||
            alert('이미지는 최대 10장까지 등록할 수 있습니다.');
          setSubmitting(false);
          return { ok: false, reason: 'too_many_images' };
        }

        const bucket = 'session-images';
        const uploaded = [];
        for (let i = 0; i < drafts.length; i += 1) {
          const draft = drafts[i];
          if (!draft?.file) continue;
          setUploadStatus(`이미지 저장중... (${i + 1}/${drafts.length})`);
          setUploadProgress(Math.round((i / Math.max(1, drafts.length)) * 100));

          const ext = getFileExt(draft.file);
          const path = `${userId}/sessions/${created.id}/${gen()}.${ext}`;
          const { error: uploadError } = await supabase.storage.from(bucket).upload(path, draft.file, {
            cacheControl: '3600',
            upsert: false,
            contentType: draft.file?.type || 'image/jpeg',
          });
          if (uploadError) {
            const msg = uploadError?.message || '이미지 업로드에 실패했습니다.';
            onNotify?.({ title: '업로드 실패', message: msg }) || alert(msg);
            setSubmitting(false);
            return { ok: false, reason: 'upload_failed' };
          }

          const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(path);
          const publicUrl = String(publicData?.publicUrl || '').trim();
          if (publicUrl) {
            uploaded.push({ id: draft.id, url: publicUrl, file: draft.file });
          }

          setUploadProgress(Math.round(((i + 1) / Math.max(1, drafts.length)) * 100));
        }

        const uploadedUrls = uploaded.map((u) => u.url);
        const finalUrls = [...keptUrls, ...uploadedUrls].filter(Boolean).slice(0, 10);
        const uploadedById = new Map(uploaded.map((u) => [u.id, u.url]));
        const coverUrl = resolveCoverUrl({ coverSelection, finalUrls, uploadedById });

        const uploadCoverThumb = async ({ coverFile }) => {
          if (!coverFile) return null;
          const thumbFile = await resizeImageToWidth(coverFile, {
            width: 100,
            mimeType: 'image/jpeg',
            quality: 0.82,
            fileName: 'thumb.jpg',
          });
          const path = `${userId}/sessions/${created.id}/${gen()}_thumb.jpg`;
          const { error: uploadError } = await supabase.storage.from(bucket).upload(path, thumbFile, {
            cacheControl: '3600',
            upsert: false,
            contentType: 'image/jpeg',
          });
          if (uploadError) {
            throw new Error(uploadError?.message || '대표 이미지 썸네일 업로드에 실패했습니다.');
          }
          const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(path);
          return String(publicData?.publicUrl || '').trim() || null;
        };

        const coverFile = resolveCoverFile({ coverSelection, coverUrl, uploaded, finalUrls });
        const coverThumbUrl = coverFile ? await uploadCoverThumb({ coverFile }) : null;

        if (finalUrls.length > 0 || coverUrl) {
          const { error: patchError } = await supabase
            .from('session_invites')
            .update({
              cover_image_url: coverUrl,
              cover_image_thumb_url: coverThumbUrl,
              image_urls: finalUrls,
            })
            .eq('id', created.id)
            .eq('client_id', userId);

          if (patchError) {
            const msg = patchError?.message || '이미지 저장에 실패했습니다.';
            onNotify?.({ title: '저장 실패', message: msg }) || alert(msg);
            setSubmitting(false);
            return { ok: false, reason: 'patch_failed' };
          }
        }

        setSubmitting(false);
        setUploadProgress(0);
        setUploadStatus('');
        await fetchEvents?.();
        return { ok: true, createdId: created.id };
      } catch (e) {
        const msg = e?.message || '등록 중 오류가 발생했습니다.';
        onNotify?.({ title: '오류', message: msg }) || alert(msg);
        setSubmitting(false);
        return { ok: false, reason: 'exception' };
      }
    },
    [fetchEvents, onNotify, submitting, type]
  );

  return {
    submitting,
    uploadProgress,
    uploadStatus,
    submitEventType,
  };
};

export default useEventTypeSubmission;
